import axios, {
    AxiosHeaders,
    type AxiosError,
    type AxiosRequestConfig,
    type InternalAxiosRequestConfig,
} from 'axios';
import router from '../router';
import { buildAuthRedirect } from '../router/guards';
import store from '../../store';
import {
    hasAuthFlag, setAuthFlag, clearAuthFlag,
    readAccessMintedAt, markAccessMinted,
    AUTH_FLAG_STORAGE_KEY,
} from './authFlag';
import { emitToast } from '@/shared/lib/toast';
import {
    ERROR_CODE_MESSAGES,
    extractErrorCode,
} from '@/shared/types/errors';

const API_URL = '/api';
const MIN_REFRESH_INTERVAL_MS = 60_000;
// SIMPLE_JWT.ACCESS_TOKEN_LIFETIME (backend/PlatformaAPP/core/settings.py).
const ACCESS_TOKEN_TTL_MS = 60 * 60_000;
// Запас: обновляемся заранее, чтобы токен не истёк, пока запрос в полёте.
const ACCESS_REFRESH_SKEW_MS = 60_000;
const CSRF_COOKIE_NAME = 'csrftoken';
const CSRF_HEADER_NAME = 'X-CSRFToken';
const SAFE_HTTP_METHODS = new Set(['get', 'head', 'options', 'trace']);

// ── Политика повторов /token/refresh/ ───────────────────────────────────────
// Сетевой сбой (ответа сервера нет) и отказ сервера — разные события: первый
// НИЧЕГО не говорит о сессии, второй означает, что её больше нет. Раньше оба
// вели к clearAuthFlag() + push('/login'), и один потерянный пакет разлогинивал
// пользователя с живой refresh-кукой (до 7 дней).
const REFRESH_NETWORK_RETRY_DELAYS_MS = [500, 1500];
// Ротация refresh (ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION) не
// синхронизирована между вкладками: проигравшая гонку вкладка приходит со
// старым токеном и получает отказ, хотя свежая кука от вкладки-победителя уже
// в браузере. Ждём, пока она приедет, и повторяем ровно один раз — повтор
// уходит уже с перечитанной кукой (её подставляет сам браузер).
// Джиттер разводит во времени вкладки, проигравшие ОДНУ И ТУ ЖЕ гонку: без
// него они стартуют повтор синхронно и сталкиваются заново.
const REFRESH_ROTATION_RACE_DELAY_MS = 400;
const REFRESH_ROTATION_RACE_JITTER_MS = 400;
// Статусы отказа refresh, которые могут быть проигранной гонкой ротации:
// 400 — legacy-контракт («Invalid refresh token»), 401 — текущий.
const REFRESH_RACE_STATUSES = new Set([400, 401]);
// Потолок ожидания в очереди 401-ретраев (E07). Щедрее axios-таймаута (15 с) с
// запасом на ретраи самого refresh: watchdog — не «нормальный» путь, а гарантия,
// что ни один запрос не останется в pending навсегда и спиннер экрана завершится.
const REFRESH_QUEUE_TIMEOUT_MS = 20_000;
const REFRESH_WATCHDOG_MESSAGE = 'Не дождались обновления сессии';

const readCookie = (name: string): string | null => {
    if (typeof document === 'undefined' || !document.cookie) {
        return null;
    }

    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
        const [rawKey, ...rawValue] = cookie.trim().split('=');
        if (rawKey === name) {
            return decodeURIComponent(rawValue.join('='));
        }
    }

    return null;
};

const ensureAxiosHeaders = (config: InternalAxiosRequestConfig): AxiosHeaders => {
    if (config.headers instanceof AxiosHeaders) {
        return config.headers;
    }

    const headers = AxiosHeaders.from(config.headers);
    config.headers = headers;
    return headers;
};

const attachCsrfHeader = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const method = (config.method ?? 'get').toLowerCase();
    if (SAFE_HTTP_METHODS.has(method)) {
        return config;
    }

    const csrfToken = readCookie(CSRF_COOKIE_NAME);
    if (!csrfToken) {
        return config;
    }

    ensureAxiosHeaders(config).set(CSRF_HEADER_NAME, csrfToken);
    return config;
};

axios.defaults.baseURL = API_URL;
axios.defaults.withCredentials = true;
axios.defaults.timeout = 15000;
axios.defaults.xsrfCookieName = CSRF_COOKIE_NAME;
axios.defaults.xsrfHeaderName = CSRF_HEADER_NAME;
const refreshClient = axios.create({
    baseURL: API_URL,
    withCredentials: true,
    timeout: 15000,
    xsrfCookieName: CSRF_COOKIE_NAME,
    xsrfHeaderName: CSRF_HEADER_NAME,
});

type RetryableRequestConfig = AxiosRequestConfig & {
    _retry?: boolean;
    /**
     * Opt-out flag for the global error-toast interceptor. Callers that show
     * inline / per-field error UI of their own should set this to ``true``
     * so the user doesn't see both an inline message and a duplicate toast.
     *
     * Правило: страница, которая рисует ошибку сама (экран-заглушка, плашка,
     * inline-сообщение) или молча деградирует, помечает свой запрос этим
     * флагом. Иначе одно и то же сообщение приходит дважды — своим текстом на
     * экране и служебным бэкендовым в тосте (#336).
     */
    suppressErrorToast?: boolean;
};
type QueueHandler = (error?: unknown) => void;

const GENERIC_ERROR_MESSAGE = 'Что-то пошло не так. Попробуйте ещё раз.';
const SERVER_ERROR_MESSAGE = 'Ошибка сервера. Мы уже разбираемся, попробуйте позже.';
const NOT_FOUND_MESSAGE = 'Не нашли то, что вы открыли. Возможно, ссылка устарела.';

/**
 * Best-effort: derive a human-readable error message from a DRF / generic
 * 4xx-5xx payload. Picks contextual copy for known error codes first, then
 * falls back to the standard ``detail`` / ``non_field_errors`` / first-key
 * shapes, and finally to a status-driven generic.
 */
const deriveErrorMessage = (status: number | undefined, data: unknown): string => {
    const code = extractErrorCode(data);
    if (code && ERROR_CODE_MESSAGES[code]) {
        return ERROR_CODE_MESSAGES[code];
    }

    if (status === 404) {
        // Текст 404 у DRF адресован разработчику и написан по-английски
        // («Not found.», «No UserProfile matches the given query», «Event not
        // found»): он называет модель, а не то, что открывал пользователь.
        // Эхоить его в toast нельзя — отвечаем своей копией. Контекстные
        // ``code``-сообщения выше остаются в силе.
        return NOT_FOUND_MESSAGE;
    }

    if (typeof data === 'string' && data.trim()) {
        // Если бэкенд отдаёт HTML-страницу (например nginx-овский «Not
        // Found» при пропавшем эндпоинте или Caddy 502), нельзя лить raw
        // HTML прямо в toast — пользователь увидит <!doctype html>...
        // в попапе. Определяем HTML по типичным префиксам и проваливаемся
        // на friendly сообщение по статусу.
        const trimmed = data.trim();
        const looksLikeHtml =
            trimmed.startsWith('<!') ||
            trimmed.startsWith('<') ||
            /^\s*<!doctype\s+html/i.test(trimmed);
        if (!looksLikeHtml) {
            return trimmed;
        }
        // intentionally fall through to status-based message below
    }

    if (data && typeof data === 'object') {
        const obj = data as Record<string, unknown>;

        const detail = obj.detail;
        if (typeof detail === 'string' && detail.trim()) {
            return detail;
        }

        const nonField = obj.non_field_errors;
        if (Array.isArray(nonField) && nonField.length > 0 && typeof nonField[0] === 'string') {
            return nonField[0] as string;
        }

        if (typeof status === 'number' && status >= 400 && status < 500) {
            // For 400-class responses, fall back to the first field-level
            // message — DRF default shape ``{ field: ["msg1", ...] }``.
            const keys = Object.keys(obj).filter(
                (k) => k !== 'code' && k !== 'detail' && k !== 'non_field_errors'
            );
            if (keys.length > 0) {
                const val = obj[keys[0]];
                if (Array.isArray(val) && val.length > 0) {
                    const first = val[0];
                    return typeof first === 'string' ? first : String(first);
                }
                if (typeof val === 'string') {
                    return val;
                }
            }
        }
    }

    if (typeof status === 'number' && status >= 500) {
        return SERVER_ERROR_MESSAGE;
    }

    return GENERIC_ERROR_MESSAGE;
};

let isRefreshing = false;
let refreshPromise: Promise<unknown> | null = null;
const refreshQueue: QueueHandler[] = [];

/**
 * Возраст access-куки. Читаем стемп каждый раз, а не кэшируем в переменной
 * модуля: куки общие на все вкладки, поэтому refresh в соседней вкладке должен
 * избавлять эту от собственного.
 */
const accessTokenAgeMs = (): number => Date.now() - readAccessMintedAt();

/**
 * Токен старше своего TTL (или момент выдачи неизвестен — сессия из времён до
 * этого кода). Неизвестность трактуем как «протух»: лишний refresh стоит один
 * запрос, а запрос с мёртвым токеном — гарантированный 401.
 */
const isAccessTokenStale = (): boolean =>
    accessTokenAgeMs() > ACCESS_TOKEN_TTL_MS - ACCESS_REFRESH_SKEW_MS;

/**
 * Разбудить всех, кто ждал refresh.
 *
 * Очередь снимаем ДО вызова колбэков (splice), а каждый колбэк — под своим
 * try/catch: синхронный выброс в одном ожидающем не должен хоронить остальную
 * очередь. `forEach` без обработчика ронял весь остаток навсегда — запросы,
 * стоявшие за упавшим, зависали в pending до конца жизни вкладки (E07).
 */
const processQueue = (error?: unknown) => {
    const pending = refreshQueue.splice(0, refreshQueue.length);
    for (const cb of pending) {
        try {
            cb(error);
        } catch (e) {
            console.error('[httpClient] refresh queue callback threw', e);
        }
    }
};

/**
 * Поставить ожидающего в очередь refresh под watchdog'ом.
 *
 * Промис ожидания — отдельный `new Promise`, его НЕ покрывает ни axios-таймаут,
 * ни таймаут самого refresh-запроса. Любой пропущенный дренаж очереди (потерянный
 * колбэк, зависший запрос) означал бы вечный спиннер у вызывающего экрана —
 * поэтому ожидающий гарантированно получает исход: результат refresh либо
 * reject по таймауту.
 */
const enqueueRefreshWaiter = (handler: QueueHandler): void => {
    let settled = false;
    let watchdog: ReturnType<typeof setTimeout> | null = null;

    const guarded: QueueHandler = (error) => {
        if (settled) return;
        settled = true;
        if (watchdog !== null) clearTimeout(watchdog);
        handler(error);
    };

    refreshQueue.push(guarded);

    watchdog = setTimeout(() => {
        const index = refreshQueue.indexOf(guarded);
        if (index !== -1) refreshQueue.splice(index, 1);
        guarded(new Error(REFRESH_WATCHDOG_MESSAGE));
    }, REFRESH_QUEUE_TIMEOUT_MS);
};

refreshClient.interceptors.request.use((config) => attachCsrfHeader(config));

const sleep = (ms: number): Promise<void> =>
    new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Сбой без ответа сервера — сеть, таймаут, отменённый запрос. Про состояние
 * сессии такой сбой не говорит ничего.
 */
const isNetworkFailure = (error: unknown): boolean =>
    !(error && typeof error === 'object' && (error as AxiosError).response);

/**
 * Один POST /token/refresh/ с политикой повторов:
 *   * сеть — повтор с нарастающей паузой (сессию нельзя терять из-за одного
 *     потерянного пакета);
 *   * отказ сервера 400/401 — ровно один повтор после короткой паузы: это
 *     может быть проигранная гонка ротации refresh между вкладками.
 */
const requestRefresh = async (): Promise<unknown> => {
    let networkAttempt = 0;
    let raceRetried = false;

    for (;;) {
        try {
            return await refreshClient.post('/token/refresh/', {});
        } catch (error) {
            if (isNetworkFailure(error)) {
                if (networkAttempt >= REFRESH_NETWORK_RETRY_DELAYS_MS.length) {
                    throw error;
                }
                await sleep(REFRESH_NETWORK_RETRY_DELAYS_MS[networkAttempt]);
                networkAttempt += 1;
                continue;
            }

            const status = (error as AxiosError).response?.status;
            if (!raceRetried && typeof status === 'number' && REFRESH_RACE_STATUSES.has(status)) {
                raceRetried = true;
                await sleep(
                    REFRESH_ROTATION_RACE_DELAY_MS
                    + Math.random() * REFRESH_ROTATION_RACE_JITTER_MS,
                );
                continue;
            }

            throw error;
        }
    }
};

/**
 * Есть ли в сторе живой пользователь.
 *
 * Отличает «сессия была и умерла» от «человек и не входил»: у гостя на
 * публичной странице 401 — штатный ответ, и уводить его на вход нельзя.
 */
const hasLiveUser = (): boolean => {
    try {
        return !!store.state.user;
    } catch {
        return false;
    }
};

/**
 * Сервер отказал в refresh — сессии действительно нет.
 *
 * Гасим и флаг, и Vuex-user: пока user жив, страница продолжает рисовать
 * дашборд, а router-гард отбрасывает с /login обратно — так и появлялся кадр
 * «живой дашборд под URL /login» (E10) и телепорт на /dashboard (E12).
 * Уходим `replace` (кнопка «назад» не должна возвращать в мёртвую сессию) и
 * СОХРАНЯЕМ текущий путь — после входа человек вернётся туда, где был.
 */
const handleSessionLost = (): void => {
    clearAuthFlag();
    try {
        store.commit('clearUser');
    } catch (e) {
        console.warn('Cannot reset user store on session loss', e);
    }

    const currentPath = window.location.pathname;
    if (currentPath === '/register' || currentPath === '/login') {
        return;
    }
    const target = buildAuthRedirect(`${currentPath}${window.location.search}`);
    void Promise.resolve(router.replace(target)).catch(() => {
        /* навигацию перебил другой переход — состояние уже сброшено */
    });
};

// Кросс-вкладочная синхронизация выхода. `storage` прилетает только в ДРУГИЕ
// вкладки того же origin, поэтому событие здесь означает ровно одно: сессию
// погасили не мы. Без этого устаревшая вкладка продолжала показывать дашборд и
// узнавала о выходе только по ошибке первого же запроса — человек успевал
// начать заполнять форму, и данные молча терялись (SESS-04, SESS-06).
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event: StorageEvent) => {
        if (event.key !== AUTH_FLAG_STORAGE_KEY) return;
        // Интересует только гашение флага. Вход в соседней вкладке
        // (newValue === '1') текущую не трогает.
        if (event.newValue !== null) return;
        if (!hasLiveUser()) return;
        handleSessionLost();
    });
}

const triggerRefresh = () => {
    if (refreshPromise) return refreshPromise;

    isRefreshing = true;
    refreshPromise = requestRefresh()
        .then((resp) => {
            markAccessMinted();
            setAuthFlag();
            window.dispatchEvent(new Event('jwt-refreshed'));
            processQueue();
            return resp;
        })
        .catch((refreshError) => {
            if (!isNetworkFailure(refreshError)) {
                // Настоящий отказ: флаг, стемп и user гасим, уводим на /login.
                // (Иначе гость на публичной странице ловил бы refresh → отказ →
                // бесконечный редирект.)
                handleSessionLost();
            }
            // Сетевой сбой: флаг НЕ трогаем и никуда не уводим — refresh-кука
            // жива, следующий запрос попробует ещё раз.
            processQueue(refreshError);
            throw refreshError;
        })
        .finally(() => {
            refreshPromise = null;
            isRefreshing = false;
        });

    return refreshPromise;
};

// Проактивный refresh: если access-токен заведомо протух, обновляем его ДО
// отправки запроса. Реактивная ветка ниже (401 → refresh → retry) сама по себе
// рабочая, но 401 успевает попасть в консоль браузера красной строкой — на
// каждый цикл обновления данных страницы. Single-flight обеспечивает
// triggerRefresh: все запросы, стартовавшие с протухшим токеном, ждут один
// общий /token/refresh/ и уходят уже со свежей кукой.
axios.interceptors.request.use(async (config) => {
    if (hasAuthFlag() && isAccessTokenStale()) {
        try {
            await triggerRefresh();
        } catch {
            // Refresh не удался. Настоящий отказ уже увёл на /login, сетевой —
            // сознательно не тронул сессию. В обоих случаях запрос отпускаем:
            // пусть отработает штатный 401-путь, а не зависнет в интерцепторе.
        }
    }
    // CSRF цепляем ПОСЛЕ refresh: ответ /token/refresh/ ротирует csrftoken,
    // и заголовок должен нести новое значение куки, а не прочитанное до.
    return attachCsrfHeader(config);
});

axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const status = error.response?.status;
        const originalRequest = error.config as RetryableRequestConfig | undefined;

        if (status === 401 && originalRequest && !originalRequest._retry) {
            originalRequest._retry = true;

            if (!hasAuthFlag()) {
                // Флага нет, а пользователь в сторе есть — значит сессию
                // погасили извне, чаще всего выходом в соседней вкладке.
                // Раньше здесь был голый reject, и вкладка продолжала рисовать
                // дашборд, показывая сырое «Учетные данные не были
                // предоставлены» вместо приглашения войти (SESS-03).
                // У гостя на публичной странице 401 — норма, его не трогаем.
                if (hasLiveUser()) {
                    handleSessionLost();
                }
                return Promise.reject(error);
            }

            // Колбэк встаёт в очередь СТРОГО до triggerRefresh(): исполнитель
            // `new Promise` синхронный, поэтому к моменту старта refresh мы уже
            // в очереди и не можем «опоздать» к её дренажу.
            const retry = new Promise((resolve, reject) => {
                enqueueRefreshWaiter((queueError) => {
                    if (queueError) {
                        reject(queueError);
                        return;
                    }
                    try {
                        resolve(axios(originalRequest));
                    } catch (retryError) {
                        // Повтор не удалось даже отправить (битый config).
                        // Реджектим ожидающего: иначе его промис остался бы в
                        // pending навсегда — тот самый вечный спиннер.
                        reject(retryError);
                    }
                });
            });

            // .catch() обязателен: исход refresh мы получаем через очередь, а
            // сам промис без обработчика всплывал бы как unhandled rejection.
            triggerRefresh().catch(() => {
                /* исход придёт ожидающему через processQueue */
            });

            return retry;
        }

        // Global error-toast surface for 4xx / 5xx that aren't a 401-refresh.
        // Callers that render their own inline error UI can opt out by
        // setting ``suppressErrorToast: true`` on their request config.
        const config = originalRequest;
        const shouldSuppress = config?.suppressErrorToast === true;
        if (!shouldSuppress && typeof status === 'number' && status >= 400) {
            // Skip 401 entirely: either the refresh above resolves the
            // request, or the caller decides what to do with the rejection.
            if (status !== 401) {
                const data = error.response?.data;
                const message = deriveErrorMessage(status, data);
                emitToast(message, status >= 500 ? 'error' : 'warning', 6000);
            }
        }

        return Promise.reject(error);
    }
);

export const ensureFreshToken = async () => {
    // Тот же гард, что у проактивного интерцептора: без флага сессии refresh
    // гарантированно получит отказ и уведёт на /login. Без гарда любой из 17
    // вызовов ensureFreshToken в api.js повторял этот редирект по кругу, и
    // самовосстановление при живой refresh-куке становилось невозможным.
    if (!hasAuthFlag()) {
        return;
    }
    if (accessTokenAgeMs() < MIN_REFRESH_INTERVAL_MS) {
        return;
    }
    await triggerRefresh();
};

export default axios;
